import React, { useState, useEffect, useRef } from 'react';
import InfoTooltip from './InfoToolTip';
import { useParams } from 'react-router-dom';
import Swal from 'sweetalert2';

const RenderInput = ({ question, sectionIndex, index, form, hanldeChange }) => {
    const { id } = useParams();
    const savedResponse = form.questions?.find(q => String(q.id) == String(question.id))?.response;
    // console.log('Saved Response:', savedResponse);
    const initialValue = question.type === 'checkbox' 
        ? (Array.isArray(savedResponse) ? savedResponse : [])
        : savedResponse !== undefined ? savedResponse : question.value ?? '';
    
    const [inputValue, setInputValue] = useState(initialValue);
    const inputRef = useRef(null);
    const prevValueRef = useRef(initialValue);

    useEffect(() => {
        if (!form || !Array.isArray(form.questions)) return;

        const currentResponse = form.questions.find(q => String(q.id) === String(question.id))?.response;
        
        let defaultValue;
        if (question.type === 'checkbox') {
            defaultValue = Array.isArray(currentResponse) ? currentResponse : JSON.parse(question.value) ?? '';
        } else if (question.type === 'radio') {
            defaultValue = currentResponse !== undefined ? currentResponse : question.value ?? '';
        } else {
            defaultValue = currentResponse !== undefined ? currentResponse : question.value ?? '';
        }

        const isExternalChange = prevValueRef.current !== defaultValue;

        if (isExternalChange && inputValue !== defaultValue) {
            setInputValue(defaultValue);
            prevValueRef.current = defaultValue;
        }
    }, [question.id, question.value, form.questions]);

    const inputName = `preg-${sectionIndex}-${index}-${question.id}-${question.question_id}-${question.type}`;

    const handleTextChange = (e) => {
        if (e.target.value.length > (question.max_characters || Infinity)) {
            return;
        }

        setInputValue(e.target.value);
        prevValueRef.current = e.target.value;
        hanldeChange(e);
    };

    const handleNumberChange = (e) => {
        console.log("Número cambiado:", e.target.value);
        const rawValue = e.target.value;
        
        // Permitimos solo números y un punto decimal
        if (rawValue === '' || /^-?\d*\.?\d*$/.test(rawValue)) {
            const numericValue =
                rawValue === '' ||
                rawValue === '-' ||
                rawValue === '.' ||
                rawValue === '-.'
                    ? null
                    : Number(rawValue);

            // Permitimos negativos si es necesario
            setInputValue(rawValue);

            const syntheticEvent = {
                ...e,
                target: {
                    ...e.target,
                    value: numericValue,
                    name: inputName,
                    dataset: { uuid: question.id, question_id: question.question_id },
                    type: 'number'
                }
            };

            hanldeChange(syntheticEvent);
        }
    };

    const handleKeyDown = (e) => {
        console.log("Validando valor en blur:", e.target.value);
        console.log("question config:", question);
        let rawValue = e.target.value;

        if (rawValue === '' || /^-?\d*\.?\d*$/.test(rawValue)) {
            const numericValue =
                rawValue === '' ||
                rawValue === '-' ||
                rawValue === '.' ||
                rawValue === '-.'
                    ? null
                    : Number(rawValue);
            // Validamos si se permite cero
            if (
                numericValue === 0 && 
                question.allow_zero === false
            ) {
                Swal.fire({
                    icon: 'warning',
                    title: 'Cero no permitido',
                    text: `El valor cero no está permitido para esta pregunta.`,
                    confirmButtonText: 'Aceptar'
                });
                return;
            }
        
            // Validamos el valor mínimo
            if (
                numericValue !== null && 
                question.min_value !== undefined &&
                numericValue < question.min_value &&
                question.allow_zero === false
            ) {
                // setInputValue(question.min_value.toString());
                Swal.fire({
                    icon: 'warning',
                    title: 'Valor mínimo excedido',
                    text: `El valor mínimo permitido es ${question.min_value}.`,
                    confirmButtonText: 'Aceptar'
                });
                return;
            }

            //Validar cuando acepta cero pero el valor es menor al minimo y distinto de cero
            if (
                numericValue !== null && 
                question.allow_zero === true &&
                numericValue !== 0 &&
                question.min_value !== undefined &&
                numericValue < question.min_value
            ) {
                Swal.fire({
                    icon: 'warning',
                    title: 'Valor mínimo excedido',
                    text: `El valor mínimo permitido es ${question.min_value}.`,
                    confirmButtonText: 'Aceptar'
                });
                return;
            }
    
            // Validamos el valor máximo
            if (
                numericValue !== null && 
                question.max_value !== undefined &&
                numericValue > question.max_value
            ) {
                Swal.fire({
                    icon: 'warning',
                    title: 'Valor máximo excedido',
                    text: `El valor máximo permitido es ${question.max_value}.`,
                    confirmButtonText: 'Aceptar'
                });
                return;
            }
    
            // Validamos el espacio decimal
            if (question.decimal_places !== undefined && question.decimal_places >= 0) {
                const parts = rawValue.split('.');
                if (parts.length === 2 && parts[1].length > question.decimal_places) {
                    const truncatedDecimal = parts[1].substring(0, question.decimal_places);
                    const newValue = `${parts[0]}.${truncatedDecimal}`;
                    setInputValue(newValue);
                    return;
                }
            }
        }

    };

    const handleCheckboxChange = (e) => {
        const optionId = e.target.dataset.option;
        const isChecked = e.target.checked;

        setInputValue(prev => {
            const newValue = isChecked 
                ? [...(Array.isArray(prev) ? prev : []), optionId]
                : (Array.isArray(prev) ? prev.filter(id => id !== optionId) : []);
            return newValue;
        });

        const syntheticEvent = {
            ...e,
            target: {
                ...e.target,
                type: 'checkbox',
                dataset: { uuid: question.id, option: optionId, question_id: question.question_id },
                checked: isChecked,
                value: isChecked 
                    ? [...(Array.isArray(inputValue) ? inputValue : []), optionId]
                    : (Array.isArray(inputValue) ? inputValue.filter(id => id !== optionId) : [])
            }
        };

        hanldeChange(syntheticEvent);
    };

    const handleRadioChange = (e) => {
        const optionId = e.target.dataset.option;
        setInputValue(optionId);

        const syntheticEvent = {
            ...e,
            target: {
                ...e.target,
                type: 'radio',
                dataset: { uuid: question.id, option: optionId, question_id: question.question_id },
                checked: true,
                value: optionId
            }
        };

        hanldeChange(syntheticEvent);
    };

    let arrayOptions = [];
    let totalOptions = question.options?.length || 0;

    if (question.type === 'checkbox' || question.type === 'radio') {
        if (question.options.length > 3) {
            arrayOptions = [question.options.slice(0, (totalOptions + 1) / 2), question.options.slice((totalOptions + 1) / 2, totalOptions)];
        } else {
            arrayOptions = [question.options];
        }
    }

    useEffect(() => {
        if (!id || !question) return;
    
        const stored = JSON.parse(sessionStorage.getItem('editReport') || '{"questions": []}');
        const alreadyStored = stored.questions.some(q => String(q.id) === String(question.id));
    
        if (!alreadyStored) {
            const updated = [
                ...stored.questions,
                {
                    id: question.id !== undefined ? question.id : null,
                    question_id: question.question_id,
                    response: question.type === 'checkbox'
                        ? JSON.parse(question.value || '[]')
                        : question.value || ''
                }
            ];
    
            sessionStorage.setItem('editReport', JSON.stringify({ questions: updated }));
            window.dispatchEvent(new Event('session-update'));
        }
    }, [id, question]);

    return (
        <div className="mb-3">
            <label className="mb-1 block font-medium text-sm text-slate-700 dark:text-white">
                <span>
                    {question.question} <span className="text-red-500">{question.required ? '*' : ''}</span>
                </span>
                {question.description && <InfoTooltip text={question.description} />}
            </label>
            <div className="relative">
                {question.type === 'text' && (
                    <input
                        ref={inputRef}
                        name={inputName}
                        type="text"
                        className="w-full text-xs md:text-sm rounded-md border border-stroke dark:border-stone-400 bg-transparent py-2 px-2 sm:pl-6 pr-10 text-black outline-none focus:border-turquesa dark:text-white focus-visible:shadow-none"
                        data-question_id={question.question_id}
                        data-uuid={question.id}
                        minLength={ question.min_characters || undefined }
                        maxLength={ question.max_characters || undefined }
                        onChange={(e) => {
                            handleTextChange(e);
                        }}
                        value={inputValue || ''}
                        placeholder={question.placeholder}
                    />
                )}
                {question.type === 'number' && (
                    <input
                        ref={inputRef}
                        name={inputName}
                        type="number"
                        // inputMode="decimal"
                        className="custom-input-number w-full text-xs md:text-sm rounded-md border border-stroke dark:border-stone-400 bg-transparent py-2 px-2 sm:pl-6 pr-10 text-black outline-none focus:border-turquesa dark:text-white focus-visible:shadow-none"
                        data-question_id={question.question_id}
                        data-uuid={question.id}
                        min={ question.min_value || undefined }
                        max={ question.max_value || undefined }
                        step={  question.decimal_places ? (1 / Math.pow(10, question.decimal_places)) : undefined }
                        onChange={handleNumberChange}
                        // onKeyDown={handleKeyDown}
                        onBlur={handleKeyDown}
                        value={inputValue}
                        placeholder={question.placeholder || '0'}
                    />
                )}
                {question.type === 'checkbox' && (
                    <div className="flex flex-wrap">
                        {arrayOptions.map((options, optIndex) => (
                            <div key={optIndex} className="w-full sm:w-1/2">
                                {options.map((option, index) => {
                                    const isChecked = Array.isArray(inputValue)
                                        ? inputValue.includes(option.id.toString())
                                        : false;
                                    return (
                                        <div key={index} className="flex items-center mr-4 mb-2">
                                            <input
                                                type="checkbox"
                                                name={inputName}
                                                checked={isChecked}
                                                data-question_id={question.question_id}
                                                data-uuid={question.id}
                                                data-option={option.id}
                                                onChange={handleCheckboxChange}
                                                className="mr-2"
                                            />
                                            <label className="text-sm text-black dark:text-white">
                                                {option.content}
                                            </label>
                                        </div>
                                    );
                                })}
                            </div>
                        ))}
                    </div>
                )}
                {question.type === 'select' && (
                    <select
                        name={inputName}
                        className="w-full text-xs md:text-sm rounded-md border border-stroke dark:border-stone-400 bg-transparent py-2 px-2 sm:pl-6 pr-10 text-black outline-none focus:border-turquesa dark:text-white focus-visible:shadow-none"
                        data-question_id={question.question_id}
                        data-uuid={question.id}
                        onChange={hanldeChange}
                        value={inputValue}
                    >
                        <option value="">Seleccione una opción</option>
                        {question.options.map((option, optIndex) => (
                            <option key={optIndex} value={option.id}>
                                {option.content}
                            </option>
                        ))}
                    </select>
                )}
                {question.type === 'textarea' && (
                    <textarea
                        ref={inputRef}
                        name={inputName}
                        className="w-full text-xs md:text-sm rounded-md border border-stroke dark:border-stone-400 bg-transparent py-2 px-2 sm:pl-6 pr-10 text-black outline-none focus:border-turquesa dark:text-white focus-visible:shadow-none"
                        data-question_id={question.question_id}
                        data-uuid={question.id}
                        onChange={(e) => {
                            setInputValue(e.target.value);
                            prevValueRef.current = e.target.value;
                            hanldeChange(e);
                        }}
                        value={inputValue}
                    />
                )}
                {question.type === 'radio' && (
                    <div className="flex flex-wrap">
                        {arrayOptions.map((options, optIndex) => (
                            <div key={optIndex} className="w-full sm:w-1/2">
                                {options.map((option, index) => {
                                    const isSelected = inputValue == option.id;
                                    return (
                                        <div key={index} className="flex items-center mr-4 mb-2">
                                            <input
                                                type="radio"
                                                name={inputName}
                                                checked={isSelected}
                                                data-question_id={question.question_id}
                                                data-uuid={question.id}
                                                data-option={option.id}
                                                onChange={handleRadioChange}
                                                className="mr-2"
                                            />
                                            <label className="text-sm text-black dark:text-white">
                                                {option.content}
                                            </label>
                                        </div>
                                    );
                                })}
                            </div>
                        ))}
                    </div>
                )}
                {question.type === 'date' && (
                    <input
                        ref={inputRef}
                        name={inputName}
                        type="date"
                        className="w-full text-xs md:text-sm rounded-md border border-stroke dark:border-stone-400 bg-transparent py-2 px-2 sm:pl-6 pr-10 text-black outline-none focus:border-turquesa dark:text-white focus-visible:shadow-none"
                        data-question_id={question.question_id}
                        data-uuid={question.id}
                        onChange={(e) => {
                            setInputValue(e.target.value);
                            prevValueRef.current = e.target.value;
                            hanldeChange(e);
                        }}
                        value={inputValue}
                    />
                )}
            </div>
        </div>
    );
};

export default RenderInput;